import { flushSync } from "react-dom";
import { placeOf, type Place, type RouteName } from "../routes.js";
import { beginCrossing, type Direction } from "./threshold.js";

/**
 * THE PLACE ON THE DOCUMENT ROOT (2026-11-02). `data-place="study"` or
 * `data-place="book"` on `<html>`, from the route alone (routes.ts
 * `placeOf`), and no attribute for a route that is neither. On the root
 * rather than on the shell, because three papers render through portals to
 * the body (the guest paper, the terms paper, the erasure dialog), the
 * settings sheet renders outside the shell, and the body's own background
 * has to follow too; anything hung on a shell element would miss them. A
 * route with no place reads the root tokens, which since the shared ink and
 * paper (2026-11-03) are the book's Flax and ink: the sign-in card, the
 * reading pages, the endgame and the settings sheet all take them.
 *
 * Called once before the first paint (main.tsx), so a cold load of the study
 * never shows one light frame, then kept current by the app on every route
 * change. Nothing here is a style: tokens.css is where a place becomes a
 * colour.
 */
export const PLACE_ATTRIBUTE = "data-place";

export function applyPlace(route: RouteName, root: Element = globalThis.document.documentElement): void {
  const place = placeOf(route);
  if (place === null) root.removeAttribute(PLACE_ATTRIBUTE);
  else root.setAttribute(PLACE_ATTRIBUTE, place);
}

/** The one browser API this module reaches for, typed here so an older lib.dom does not decide whether it exists. */
interface DocumentWithViewTransition {
  startViewTransition?: (update: () => void) => unknown;
}

/** What a started transition hands back, as far as this module reads it: two promises that reject when a newer transition interrupts it. */
interface ViewTransitionLike {
  ready?: unknown;
  finished?: unknown;
  updateCallbackDone?: unknown;
}

/**
 * Which way a crossing between the two places goes, or null for a change
 * that is not between them: within one place, or to or from a door (the
 * sign-in card, a reading page), which keeps the plain fade (decision 13 of
 * the 2026-11-03 plan).
 */
export function thresholdDirection(from: Place | null, to: Place | null): Direction | null {
  if (from === null || to === null || from === to) return null;
  return to === "book" ? "to-book" : "to-study";
}

/** An interrupted transition rejects its promises; that is the ordinary end of a crossing a newer one supersedes, not an error. */
function settle(transition: unknown): void {
  if (transition === null || typeof transition !== "object") return;
  const t = transition as ViewTransitionLike;
  for (const p of [t.ready, t.finished, t.updateCallbackDone]) {
    if (p instanceof Promise) p.catch(() => undefined);
  }
}

/**
 * THE THRESHOLD (2026-11-02; one owner and the dissolve since 2026-11-04).
 * A route change that crosses from one place to the other — the study to
 * the book, or back — is a large change of brightness, and the design asks
 * that it be softened without a white flash and stay understandable with
 * motion off. So: when the browser has view transitions and nothing has
 * asked for stillness, the change is applied inside one. Between the study
 * and the book it is the 620ms dissolve (threshold.ts; the root ramps, the
 * backdrop and the overlays' blending are the stylesheet's, under
 * `html[data-crossing]`); to or from a door it is the plain cross-fade at
 * `--pc-dur-slow`. The attribute is written inside the same callback, so the
 * new snapshot already wears the new place; the app's own effect writes the
 * same value again afterwards and changes nothing. A change within one
 * place, a browser without the API, or a reader who asked for stillness:
 * the change is applied at once, exactly as before.
 *
 * ONE OWNER. The hash is the router, and the `hashchange` sync is the only
 * caller for a navigation: a helper that wants to move sets the hash and
 * nothing else, so one crossing starts one transition (before 2026-11-04 a
 * helper applied its change itself and the sync applied it again a task
 * later, two transitions at once, the second capturing the already-new page
 * as its old state: no fade, and an interruption error in the console). A
 * newer crossing supersedes the one under way: its overlays are removed
 * first, and the promises the interrupted transition rejects are settled
 * here, so an ordinary rapid double navigation leaves no error behind.
 */
export function acrossThePlace(from: RouteName, to: RouteName, apply: () => void): void {
  const doc = globalThis.document as Document & DocumentWithViewTransition;
  const crossing = placeOf(from) !== placeOf(to);
  const still = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
  if (!crossing || still || typeof doc.startViewTransition !== "function") {
    apply();
    return;
  }
  const direction = thresholdDirection(placeOf(from), placeOf(to));
  const controller = direction === null ? null : beginCrossing(direction, doc);
  try {
    const transition = doc.startViewTransition(() => {
      flushSync(apply);
      applyPlace(to);
      controller?.mount();
    });
    settle(transition);
    controller?.start(transition);
  } catch {
    // The API refused (a document not fully active, say): the change still lands, with no dissolve.
    controller?.cancel();
    apply();
    applyPlace(to);
  }
}
